<template>
	<div>
		<internal-submenu />

		<core-layout-content full>
			<!-- <core-layout-headline-and-actions>
			<template slot="headline">{{ $t("participant") }}</template>
			</core-layout-headline-and-actions>-->

			<!-- Invite - display only if participant doesn't have login yet -->
			<j-card class="xs-12 bg5 r2 p4 mb4">
				<div v-if="hasAssociatedLogin">
					<div class="flex xs-between">
						<j-tag :label="$t('approved')" variant="success" />
						<j-button
							:to="{ name: 'UsersUpdate', params: { loginId } }"
							type="tertiary"
							>{{ $t("go_to_user") }}</j-button
						>
					</div>
				</div>
				<div v-else>
					<div class="flex pb2">
						<j-tag :label="$t('pending')" variant="neutral" />
					</div>
					<div class="lh125 c6">{{ $t("invite_participant_description") }}</div>
					<j-button
						@click="$modal.show('inviteInternal')"
						class="mt4"
						size="small"
						>{{ $t("approve") }}</j-button
					>
				</div>
			</j-card>

			<!-- organizations -->
			<j-card class="xs-12 bg5 mb4 p4">
				<div class="flex xs-12 xs-between xs-middle">
					<div class="fw7">{{ $t("organizations") }}</div>
					<j-button
						size="xsmall"
						type="tertiary"
						@click="$modal.show('addOrganizationModal')"
						>{{ $t("add_organization") }}</j-button
					>
				</div>
				<div class="r2 b1 bc7 mt4">
					<div
						v-for="(rel, i) in filteredRelationships"
						:key="i"
						class="flex pt2 pb2 xs-between xs-middle"
						:class="{ 'bb1 bc7': i < filteredRelationships.length - 1 }"
					>
						<div class="fw7 fs3 c6 pl2">{{ rel.entity.name }}</div>
						<div class="cu2 pr2" @click="removeOrganization(rel)">
							<crm-icon name="delete" color="cError" />
						</div>
					</div>
					<div
						class="flex xs-middle xs-center p4 c6 fs3"
						v-if="!filteredRelationships.length"
					>
						{{ $t("errors.no_data") }}
					</div>
				</div>
			</j-card>

			<!-- Content -->
			<div class="flex wrap xs-12 mb4">
				<core-entity-card
					:entity="fetchedInternal"
					:pinnable="false"
					:hasActions="true"
					@added="fetchInternal"
					@updated="fetchInternal"
					@deleted="fetchInternal"
				/>
			</div>

			<!-- Footer menu -->
			<div class="flex wrap xs-12 xs-right bt1 bc7 pt2 mt8">
				<j-button
					type="error"
					@click="$modal.show('j-dialog')"
					v-can="'writeInternals'"
				>
					<crm-icon name="delete" size="normal" color="c5" class="mr1" />
					{{ $t("delete_participant") }}
				</j-button>
			</div>

			<!-- Delete Confirmation Modal -->
			<j-dialog
				:title="$t('delete_participant')"
				type="delete"
				@confirm="onDelete(id)"
				>{{ $t("errors.are_you_sure_you_want_to_delete_internal") }}</j-dialog
			>

			<!-- Modal to invite -->
			<core-modal name="inviteInternal" :headline="$t('invite_participant')">
				<apeiron-invite-internal
					:internalId="id"
					:prepopulateEmail="internalEmail"
					@invite:finished="
						$modal.hide('inviteInternal')
						$router.push({ name: 'InternalsList' })
					"
				/>
			</core-modal>

			<!-- Modal to add organization -->
			<core-modal
				name="addOrganizationModal"
				:headline="$t('add_organization')"
			>
				<div class="p4">
					<div class="fw7 fs4 pb4">{{ $t("organization") }}</div>
					<core-select-organizations
						v-model="addOrganizationInput"
						class="mb15"
					/>
					<j-button class="mt15" @click="addOrganization">
						{{ $t("add") }}
					</j-button>
				</div>
			</core-modal>
		</core-layout-content>
	</div>
</template>

<script>
import CoreLayoutContent from "@/modules/core/components/CoreLayoutContent"
import CoreEntityCard from "@/modules/core/components/CoreEntityCard/"
import CoreModal from "@/modules/core/components/CoreModal/"
import CoreSelectOrganizations from "@/modules/core/components/CoreSelectOrganizations"
import ApeironInviteInternal from "./ApeironInviteInternal"
import JCard from "@/components/JCard"
import JButton from "@/components/JButton"
import JDialog from "@/components/JDialog"
import CrmIcon from "@/components/CrmIcon"
import oldApi from "../../api"
import api from "@/api/gql.js"
import { gql } from "@/api/utils.js"
import get from "lodash.get"
import JTag from "@/components/JTag"
import InternalSubmenu from "./InternalSubmenu"

export default {
	components: {
		CoreLayoutContent,
		CoreEntityCard,
		JCard,
		JButton,
		CrmIcon,
		JDialog,
		CoreModal,
		ApeironInviteInternal,
		JTag,
		CoreSelectOrganizations,
		InternalSubmenu
	},

	data() {
		return {
			id: this.$route.params.id,
			fetchedInternal: {},
			addOrganizationInput: []
		}
	},

	methods: {
		async fetchInternal() {
			this.isLoading = true
			const query = gql`
				query internal(
					$skip: Int
					$limit: Int
					$sort: sortInput
					$where: internalWhereInput
				) {
					internals(skip: $skip, limit: $limit, sort: $sort, where: $where) {
						totalCount
						list {
							__typename
							id
							createdAt
							name
							englishFirstName
							englishLastName
							chineseFirstName
							chineseLastName
							internalCode
							isActive
							associatedLogin {
								id
							}
							contacts {
								id
								type
								value
							}
							facts {
								type
								value {
									stringValue
								}
							}
							relationships {
								type
								entity {
									name
									id
								}
							}
							subscriptions {
								id
								startDate
								endDate
								createdAt
								offers {
									product {
										productId {
											plan
											type
											version
										}
									}
								}
							}
						}
					}
				}
			`

			const variables = {
				where: { id: this.id }
			}
			const res = await api({ query, variables })
			this.isLoading = false

			// Check errors
			if (res.errors) {
				this.$store.dispatch("showError", res.errors)
				return
			}

			// If it doesn't exist, redirect
			if (res.data.internals.list.length === 0)
				this.$router.push({ name: "InternalsList" })

			// Assign
			this.fetchedInternal = res.data.internals.list[0]
		},

		async addOrganization() {
			if (!this.addOrganizationInput?.length) {
				this.$store.dispatch("showErrors", [
					this.$t("errors.no_organization_selected")
				])
				return
			}
			const query = gql`
				mutation addLink($input: createLinkInput!) {
					addLink(linkInput: $input) {
						errors
						errors_2 {
							code
							message
						}
						status
					}
				}
			`
			const variables = {
				input: {
					sourceId: this.id,
					link: "worksFor",
					targetId: this.addOrganizationInput[0]?.id
				}
			}
			const res = await api({ query, variables })
			if (res?.data?.addLink?.status !== "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text:
							res?.errors ||
							res?.data?.addLink?.errors ||
							res?.data?.addLink?.errors_2?.message
					}
				})
			} else {
				this.addOrganizationInput = []
				this.$modal.hide("addOrganizationModal")
				this.fetchInternal()
			}
		},

		async removeOrganization(rel) {
			const query = gql`
				mutation removeLink($input: removeLinkInput!) {
					removeLink(linkInput: $input) {
						errors
						errors_2 {
							code
							message
						}
						status
					}
				}
			`
			const variables = {
				input: {
					sourceId: this.id,
					link: rel?.type,
					targetId: rel?.entity?.id
				}
			}
			const res = await api({ query, variables })
			if (res?.data?.removeLink?.status !== "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text:
							res?.errors ||
							res?.data?.removeLink?.errors ||
							res?.data?.removeLink?.errors_2?.message
					}
				})
			} else {
				this.fetchInternal()
			}
		},

		async onDelete(id) {
			this.isLoading = true
			const res = await oldApi.deleteInternal({ id })
			this.isLoading = false

			// Error
			if (res.errors) {
				this.$store.dispatch("showError", res.errors)
			}

			// go back to previous page
			if (res) this.$router.push(`/${this.$store.state.tenantId}/internals/`)
			this.$store.dispatch("addMessage", {
				message: {
					type: "success",
					text: this.$t("participant_deleted")
				}
			})
		}
	},

	computed: {
		hasAssociatedLogin() {
			return get(this, "fetchedInternal.associatedLogin.id") ? true : false
		},

		loginId() {
			return this.fetchedInternal?.associatedLogin?.id
		},
		// subscriptionType() {
		// 	return (
		// 		this.fetchedInternal?.facts?.find(
		// 			fact => fact.type === "subscriptionType"
		// 		)?.value?.stringValue || "N/A"
		// 	)
		// },
		subscriptions() {
			return this.fetchedInternal?.subscriptions
		},
		filteredRelationships() {
			return this.fetchedInternal?.relationships?.filter(
				rel => rel.type === "worksFor"
			)
		},
		internalEmail() {
			return this.fetchedInternal?.contacts?.find(
				contact => contact.type === "email"
			)?.value
		}
	},

	created() {
		this.fetchInternal()
	}
}
</script>

<style></style>
